import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { HerramientasCvu } from '../components/padron/HerramientasCvu';
import { CvuResumenActivos } from '../components/padron/CvuResumenActivos';
import { PadronFiltroSponsorAnterior } from '../components/padron/PadronFiltroSponsorAnterior';
import { esCvuActivo } from '../components/padron/cvuUtils';
import {
  Upload,
  FileSpreadsheet,
  Users,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Copy,
  Check,
  Eye,
  MessageSquare,
  CreditCard,
  Building2,
  User,
  RefreshCw,
  Download,
  Filter,
  ChevronRight,
  Clock,
  ArrowRight,
  Shield,
  ShieldOff,
  Wrench
} from 'lucide-react';

// Componente para copiar sin caracteres especiales
const CopyButton = ({ value, cleanFn }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    const cleanValue = cleanFn ? cleanFn(value) : value;
    await navigator.clipboard.writeText(cleanValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0 ml-1"
      onClick={handleCopy}
      title="Copiar sin caracteres especiales"
    >
      {copied ? (
        <Check size={12} className="text-green-600" />
      ) : (
        <Copy size={12} className="text-slate-400" />
      )}
    </Button>
  );
};

// Limpiar funciones
const limpiarDni = (dni) => dni?.replace(/\./g, '') || '';
const limpiarCuit = (cuit) => cuit?.replace(/-/g, '') || '';
const limpiarCvu = (cvu) => cvu?.replace(/-/g, '') || '';

const CAMPO_LABELS = {
  dni_limpio: 'DNI',
  dni: 'DNI',
  razon_social: 'Nombre / Razón Social',
  fecha_nacimiento: 'Fecha de nacimiento',
  nacionalidad: 'Nacionalidad',
  genero: 'Género',
  pep_uif: 'PEP (UIF)',
  tipo: 'Tipo de persona',
  cuit: 'CUIT',
  cuit_limpio: 'CUIT',
};


const PadronInterno = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esAdmin = ['ADMIN', 'SUPERADMIN', 'ADMINISTRADOR'].includes((user?.rol || '').toUpperCase());
  const [activeTab, setActiveTab] = useState('lista');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Estado para lista de clientes
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendientesRevision, setPendientesRevision] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(0);
  const [stats, setStats] = useState(null);
  
  // Filtros avanzados
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroCvuPrefijo, setFiltroCvuPrefijo] = useState('');
  const [filtroCvuActivo, setFiltroCvuActivo] = useState('');
  const [filtroEstadoCliente, setFiltroEstadoCliente] = useState('');
  const [filtroWaya, setFiltroWaya] = useState('');
  const [filtroSponsor, setFiltroSponsor] = useState('');
  const [soloSponsorAnterior, setSoloSponsorAnterior] = useState(false);
  const [bancosSponsor, setBancosSponsor] = useState([]);
  const [exportando, setExportando] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  
  // Selección masiva
  const [seleccionados, setSeleccionados] = useState([]);
  const [seleccionarTodos, setSeleccionarTodos] = useState(false);
  const [showAccionMasiva, setShowAccionMasiva] = useState(false);
  const [accionMasivaTipo, setAccionMasivaTipo] = useState('');
  
  // Estado para resultado de importación
  const [importResult, setImportResult] = useState(null);
  const [showImportResult, setShowImportResult] = useState(false);
  
  // Estado para detalle de cliente
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Estado para revisión
  const [clientesRevision, setClientesRevision] = useState([]);
  
  // Drag & drop
  const [isDragging, setIsDragging] = useState(false);

  // Reconciliación con Reporte de Billeteras
  const [reconFile, setReconFile] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconResult, setReconResult] = useState(null);
  const [reconApplied, setReconApplied] = useState(false);

  useEffect(() => {
    loadClientes();
    loadStats();
  }, [page, busqueda, filtroTipo, filtroCvuPrefijo, filtroCvuActivo, filtroEstadoCliente, filtroWaya, filtroSponsor, soloSponsorAnterior]);

  useEffect(() => {
    api.get('/system-config/bancos-sponsor').then((r) => setBancosSponsor(r.data?.bancos || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab === 'revision') {
      loadClientesRevision();
    }
  }, [activeTab]);

  // Limpiar selección cuando cambian los clientes
  useEffect(() => {
    setSeleccionados([]);
    setSeleccionarTodos(false);
  }, [clientes]);

  const filtrosParams = () => ({
    busqueda: busqueda || undefined,
    tipo: filtroTipo || undefined,
    cvu_prefijo: filtroCvuPrefijo || undefined,
    estado_cvu: filtroCvuActivo || undefined,
    banco_sponsor: filtroSponsor || undefined,
    solo_sponsor_anterior_activo: soloSponsorAnterior || undefined,
    estado_cliente: filtroEstadoCliente || undefined,
    waya_actualizado: filtroWaya || undefined
  });

  const descargarExcel = async () => {
    setExportando(true);
    try {
      const res = await api.get('/padron-interno/exportar-excel', { params: filtrosParams(), responseType: 'blob', timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = (res.headers['content-disposition'] || '').split('filename=')[1] || 'padron_cvus.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado con los filtros aplicados');
    } catch (error) {
      toast.error('No se pudo generar el Excel');
    } finally {
      setExportando(false);
    }
  };

  const loadClientes = async () => {
    setLoading(true);
    try {
      const params = { skip: page * 50, limit: 50, ...filtrosParams() };
      const res = await api.get('/padron-interno', { params });
      setClientes(res.data.clientes || []);
      setTotal(res.data.total || 0);
      setPendientesRevision(res.data.pendientes_revision || 0);
    } catch (error) {
      console.error('Error loading clientes:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get('/padron-interno/estadisticas');
      setStats(res.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadClientesRevision = async () => {
    try {
      const res = await api.get('/padron-interno/pendientes-revision');
      setClientesRevision(res.data.clientes || []);
    } catch (error) {
      console.error('Error loading revision:', error);
    }
  };

  // Funciones de selección
  const toggleSeleccion = (cuitLimpio) => {
    setSeleccionados(prev => {
      if (prev.includes(cuitLimpio)) {
        return prev.filter(c => c !== cuitLimpio);
      } else {
        return [...prev, cuitLimpio];
      }
    });
  };

  const toggleSeleccionarTodos = () => {
    if (seleccionarTodos) {
      setSeleccionados([]);
    } else {
      setSeleccionados(clientes.map(c => c.cuit_limpio));
    }
    setSeleccionarTodos(!seleccionarTodos);
  };

  // Acciones masivas
  const [updatingWayaBulk, setUpdatingWayaBulk] = useState(false);

  const ejecutarAccionMasiva = async (tipo, opciones = {}) => {
    if (seleccionados.length === 0) {
      toast.error('Seleccione al menos un cliente');
      return;
    }

    try {
      let res;
      if (tipo === 'activar_cvus' || tipo === 'desactivar_cvus') {
        res = await api.post('/padron-interno/acciones-masivas/cvus', {
          cuits: seleccionados,
          cvu_prefijo: opciones.cvuPrefijo || null,
          nuevo_estado_cvu: tipo === 'activar_cvus',
          afectar_todos_cvus: !opciones.cvuPrefijo
        });
      } else if (tipo === 'cambiar_estado_cliente') {
        res = await api.post('/padron-interno/acciones-masivas/clientes', {
          cuits: seleccionados,
          nuevo_estado_cliente: opciones.nuevoEstado,
          propagar_a_cvus: opciones.propagarCvus !== false
        });
      }
      
      toast.success(res.data.message);
      loadClientes();
      setSeleccionados([]);
      setShowAccionMasiva(false);
    } catch (error) {
      console.error('Error en acción masiva:', error);
      toast.error('Error al ejecutar acción masiva');
    }
  };

  const handleBulkWayaUpdate = async () => {
    if (seleccionados.length === 0) {
      toast.error('Seleccione al menos un cliente');
      return;
    }
    setUpdatingWayaBulk(true);
    try {
      const res = await api.post('/waya/actualizar-datos-cuentas', {
        cuits: seleccionados,
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        // Poll job status
        const jobId = res.data.job_id;
        const pollInterval = setInterval(async () => {
          try {
            const jobRes = await api.get(`/waya/job/${jobId}`);
            if (jobRes.data?.estado === 'COMPLETADO') {
              clearInterval(pollInterval);
              setUpdatingWayaBulk(false);
              toast.success(`Actualización completada: ${jobRes.data.exitosos} exitosos, ${jobRes.data.errores} errores`);
              loadClientes();
              setSeleccionados([]);
            }
          } catch { /* keep polling */ }
        }, 5000);
        setTimeout(() => { clearInterval(pollInterval); setUpdatingWayaBulk(false); }, 300000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar actualización Waya');
      setUpdatingWayaBulk(false);
    }
  };

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroTipo('');
    setFiltroCvuPrefijo('');
    setFiltroCvuActivo('');
    setFiltroEstadoCliente('');
    setFiltroWaya('');
    setFiltroSponsor('');
    setSoloSponsorAnterior(false);
    setPage(0);
  };

  // Drag & Drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input para permitir seleccionar el mismo archivo
    e.target.value = '';
  };

  const handleFileUpload = async (file) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('El archivo debe ser Excel (.xlsx o .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Inicia el procesamiento en segundo plano (evita timeout 524 de Cloudflare)
      const res = await api.post('/padron-interno/importar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No se pudo iniciar la importación');

      // Polling del resultado
      let job = null;
      for (let i = 0; i < 900; i++) { // hasta ~30 min
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const jr = await api.get(`/padron-interno/importar-job/${jobId}`, { timeout: 20000 });
          job = jr.data;
          if (job.estado === 'completado' || job.estado === 'error') break;
        } catch { /* reintentar */ }
      }

      if (!job || job.estado === 'error') {
        toast.error(job?.error || 'Error al procesar el padrón');
        return;
      }

      const resultado = job.resultado || {};
      setImportResult(resultado);
      setShowImportResult(true);
      await loadClientes();
      await loadStats();

      const { clientes_nuevos = 0, clientes_actualizados = 0, para_revision = 0 } = resultado;
      toast.success(
        `Importación completada: ${clientes_nuevos} nuevos, ${clientes_actualizados} actualizados, ${para_revision} para revisión`,
        { duration: 5000 }
      );
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(error.response?.data?.detail || 'Error al importar archivo');
    } finally {
      setUploading(false);
    }
  };

  const pollReconJob = async (jobId) => {
    for (let i = 0; i < 300; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const jr = await api.get(`/padron-interno/importar-job/${jobId}`, { timeout: 20000 });
        if (jr.data?.estado === 'completado' || jr.data?.estado === 'error') return jr.data;
      } catch { /* reintentar */ }
    }
    return null;
  };

  const runReconciliacion = async (file, soloPreview) => {
    setReconLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(
        `/padron-interno/reconciliar-billeteras?solo_preview=${soloPreview}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No se pudo iniciar la reconciliación');
      const job = await pollReconJob(jobId);
      if (!job || job.estado === 'error') {
        toast.error(job?.error || 'Error en la reconciliación');
        return;
      }
      setReconResult(job.resultado || {});
      if (!soloPreview) {
        setReconApplied(true);
        toast.success(job.resultado?.mensaje || 'Reconciliación aplicada', { duration: 6000 });
        await loadClientes();
        await loadStats();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al reconciliar');
    } finally {
      setReconLoading(false);
    }
  };

  const handleReconSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('El archivo debe ser Excel (.xlsx o .xls)');
      return;
    }
    setReconFile(file);
    setReconResult(null);
    setReconApplied(false);
    runReconciliacion(file, true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadClientes();
  };

  const handleVerCliente = (cliente) => {
    // Navegar a la página de detalle del cliente
    const moduleBase = window.location.pathname.split('/')[2] || 'clientes';
    navigate(`/m/${moduleBase}/cliente/${cliente.cuit}`);
  };

  const handleActualizarComentario = async (cuit, cvu, comentario) => {
    try {
      await api.put(`/padron-interno/cliente/${cuit}/cvu/${cvu}/comentario`, { comentario });
      toast.success('Comentario actualizado');
      loadClientes();
    } catch (error) {
      toast.error('Error al actualizar comentario');
    }
  };

  const handleCambiarEstadoCvu = async (cuit, cvu, activo) => {
    try {
      await api.put(`/padron-interno/cliente/${cuit}/cvu/${cvu}/estado`, { activo });
      toast.success(`CVU ${activo ? 'activado' : 'desactivado'}`);
      loadClientes();
      if (selectedCliente) {
        const updated = await api.get(`/padron-interno/cliente/${cuit}`);
        setSelectedCliente(updated.data);
      }
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleResolverRevision = async (cuit, accion) => {
    try {
      await api.post(`/padron-interno/cliente/${cuit}/resolver-revision`, { accion });
      toast.success('Revisión resuelta');
      loadClientesRevision();
      loadClientes();
      loadStats();
    } catch (error) {
      toast.error('Error al resolver revisión');
    }
  };

  const [recalculando, setRecalculando] = useState(false);
  const handleRecalcularRevisiones = async () => {
    setRecalculando(true);
    try {
      const res = await api.post('/padron-interno/recalcular-revisiones');
      const r = res.data || {};
      toast.success(`Limpieza lista: ${r.auto_resueltos || 0} revisiones triviales resueltas. Quedan ${r.pendientes_restantes || 0} con cambios reales.`);
      loadClientesRevision();
      loadStats();
    } catch (error) {
      toast.error('Error al recalcular revisiones');
    } finally {
      setRecalculando(false);
    }
  };

  const handleEliminarSinCuit = async (cuitLimpio) => {
    if (!confirm('¿Estás seguro de eliminar este registro sin CUIT?')) return;
    
    try {
      await api.delete(`/padron-interno/cliente/${encodeURIComponent(cuitLimpio)}`);
      toast.success('Registro eliminado');
      loadClientesRevision();
      loadStats();
    } catch (error) {
      toast.error('Error al eliminar registro');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users size={24} className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Padrón de Clientes</h1>
            <p className="text-sm text-slate-500">Gestión del padrón interno de clientes y CVUs</p>
          </div>
        </div>
        
        {/* Stats rápidos */}
        {stats && (
          <div className="flex gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.total_clientes}</p>
              <p className="text-xs text-slate-500">Clientes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.cvus?.activos || 0}</p>
              <p className="text-xs text-slate-500">CVUs Activos</p>
            </div>
            {stats.pendientes_revision > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.pendientes_revision}</p>
                <p className="text-xs text-slate-500">Para Revisión</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="lista" className="flex items-center gap-2">
            <Users size={16} />
            Lista de Clientes
          </TabsTrigger>
          <TabsTrigger value="cargar" className="flex items-center gap-2">
            <Upload size={16} />
            Cargar Excel
          </TabsTrigger>
          <TabsTrigger value="revision" className="flex items-center gap-2">
            <AlertTriangle size={16} />
            Revisión
            {pendientesRevision > 0 && (
              <Badge className="ml-1 bg-amber-500">{pendientesRevision}</Badge>
            )}
          </TabsTrigger>
          {esAdmin && (
            <TabsTrigger value="herramientas-cvu" className="flex items-center gap-2" data-testid="tab-herramientas-cvu">
              <Wrench size={16} />
              Herramientas de CVU
            </TabsTrigger>
          )}
        </TabsList>

        {/* Tab: Lista de Clientes */}
        <TabsContent value="lista" className="mt-4">
          {/* Buscador */}
          <Card className="p-4 mb-4">
            <form onSubmit={handleSearch} className="flex gap-4">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por CUIT, DNI, nombre o CVU..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" disabled={loading}>
                <Search size={16} className="mr-2" />
                Buscar
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setMostrarFiltros(!mostrarFiltros)}
              >
                <Filter size={16} className="mr-2" />
                Filtros {mostrarFiltros ? '▲' : '▼'}
              </Button>
              <Button type="button" variant="outline" onClick={descargarExcel} disabled={exportando} data-testid="padron-descargar-excel">
                {exportando ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
                Descargar Excel
              </Button>
            </form>
            <PadronFiltroSponsorAnterior checked={soloSponsorAnterior} onChange={(v) => { setSoloSponsorAnterior(v); setPage(0); }} />

            {/* Filtros avanzados */}
            {mostrarFiltros && (
              <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Tipo de persona</Label>
                  <select
                    value={filtroTipo}
                    onChange={(e) => { setFiltroTipo(e.target.value); setPage(0); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="Persona Física">Persona Física</option>
                    <option value="Persona Jurídica">Persona Jurídica</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">CVUs que empiecen con</Label>
                  <Input
                    placeholder="Ej: 00003573"
                    value={filtroCvuPrefijo}
                    onChange={(e) => { setFiltroCvuPrefijo(e.target.value); setPage(0); }}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Estado CVU</Label>
                  <select
                    value={filtroCvuActivo}
                    onChange={(e) => { setFiltroCvuActivo(e.target.value); setPage(0); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="filtro-estado-cvu"
                  >
                    <option value="">Todos</option>
                    <option value="activos">Activos</option>
                    <option value="inactivos">Inactivos</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Banco sponsor del CVU</Label>
                  <select
                    value={filtroSponsor}
                    onChange={(e) => { setFiltroSponsor(e.target.value); setPage(0); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="filtro-banco-sponsor"
                  >
                    <option value="">Todos</option>
                    {bancosSponsor.map((b) => (
                      <option key={b.prefijo} value={b.prefijo}>{b.nombre}{b.activo ? '' : ' (anterior)'}</option>
                    ))}
                    <option value="desconocido">Desconocido</option>
                  </select>
                  {filtroSponsor && filtroCvuActivo && (
                    <p className="text-[11px] text-slate-400 mt-1">Banco y estado se cumplen en el mismo CVU.</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Estado cliente</Label>
                  <select
                    value={filtroEstadoCliente}
                    onChange={(e) => { setFiltroEstadoCliente(e.target.value); setPage(0); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="ACTIVO">Activo</option>
                    <option value="INACTIVO">Inactivo</option>
                    <option value="OBSERVADO">Observado</option>
                    <option value="SUSPENDIDO">Suspendido</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Datos Waya</Label>
                  <select
                    value={filtroWaya}
                    onChange={(e) => { setFiltroWaya(e.target.value); setPage(0); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    data-testid="filtro-waya"
                  >
                    <option value="">Todos</option>
                    <option value="si">Actualizados</option>
                    <option value="no">Sin actualizar</option>
                  </select>
                </div>
                <div className="col-span-2 md:col-span-4 flex justify-end">
                  <Button variant="ghost" size="sm" onClick={limpiarFiltros}>
                    Limpiar filtros
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Barra de acciones masivas */}
          {seleccionados.length > 0 && (
            <Card className="p-3 mb-4 bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-800">
                  {seleccionados.length} cliente(s) seleccionado(s)
                </span>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => ejecutarAccionMasiva('activar_cvus', { cvuPrefijo: filtroCvuPrefijo })}
                  >
                    Activar CVUs
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => ejecutarAccionMasiva('desactivar_cvus', { cvuPrefijo: filtroCvuPrefijo })}
                  >
                    Desactivar CVUs
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => ejecutarAccionMasiva('cambiar_estado_cliente', { nuevoEstado: 'ACTIVO' })}
                    className="text-green-600 border-green-300"
                  >
                    Activar Clientes
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => ejecutarAccionMasiva('cambiar_estado_cliente', { nuevoEstado: 'INACTIVO' })}
                    className="text-red-600 border-red-300"
                  >
                    Desactivar Clientes
                  </Button>
                  <Button 
                    size="sm" 
                    disabled={updatingWayaBulk}
                    onClick={handleBulkWayaUpdate}
                    style={{ backgroundColor: '#059669', color: '#ffffff' }}
                    data-testid="btn-bulk-waya-update"
                  >
                    <RefreshCw size={14} className={`mr-1 ${updatingWayaBulk ? 'animate-spin' : ''}`} />
                    {updatingWayaBulk ? 'Actualizando...' : 'Actualizar datos Waya'}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setSeleccionados([])}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Tabla de clientes */}
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <input 
                      type="checkbox"
                      checked={seleccionarTodos}
                      onChange={toggleSeleccionarTodos}
                      className="w-4 h-4 rounded"
                    />
                  </TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>CUIT</TableHead>
                  <TableHead>DNI</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>F. Nacimiento</TableHead>
                  <TableHead>Nacionalidad</TableHead>
                  <TableHead>Género</TableHead>
                  <TableHead>CVUs</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8">
                      <RefreshCw size={24} className="animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : clientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      No hay clientes en el padrón. Cargá un archivo Excel para comenzar.
                    </TableCell>
                  </TableRow>
                ) : (
                  clientes.map((cliente) => (
                    <TableRow key={cliente.cuit_limpio} className={cliente.necesita_revision ? 'bg-amber-50' : ''}>
                      <TableCell>
                        <input 
                          type="checkbox"
                          checked={seleccionados.includes(cliente.cuit_limpio)}
                          onChange={() => toggleSeleccion(cliente.cuit_limpio)}
                          className="w-4 h-4 rounded"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {cliente.tipo === 'Persona Jurídica' ? (
                            <Building2 size={16} className="text-purple-600" />
                          ) : (
                            <User size={16} className="text-blue-600" />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{cliente.razon_social}</p>
                            {cliente.necesita_revision && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Revisar</Badge>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <span className="font-mono text-sm">{cliente.cuit}</span>
                          <CopyButton value={cliente.cuit} cleanFn={limpiarCuit} />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <span className="font-mono text-sm">{cliente.dni || '-'}</span>
                          {cliente.dni && <CopyButton value={cliente.dni} cleanFn={limpiarDni} />}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          cliente.tipo === 'Persona Jurídica' 
                            ? 'bg-purple-50 text-purple-700' 
                            : 'bg-blue-50 text-blue-700'
                        }>
                          {cliente.tipo === 'Persona Jurídica' ? 'Empresa' : 'Persona'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{cliente.fecha_nacimiento || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{cliente.nacionalidad || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {cliente.genero === 'M' ? 'M' : cliente.genero === 'F' ? 'F' : cliente.genero === 'X' ? 'X' : cliente.genero || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <CreditCard size={14} className="text-slate-400" />
                          <span className="font-medium">
                            {cliente.cvus?.filter(esCvuActivo).length || 0}
                          </span>
                          <span className="text-slate-400">/ {cliente.cvus?.length || 0}</span>
                        </div>
                        <CvuResumenActivos cvus={cliente.cvus || []} compacto testid={`padron-cvus-resumen-${cliente.cuit_limpio}`} />
                      </TableCell>
                      <TableCell>
                        {cliente.pep_uif ? (
                          <Badge className="bg-red-100 text-red-700">PEP</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">Normal</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleVerCliente(cliente)}
                        >
                          <Eye size={16} className="mr-1" />
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            
            {/* Paginación */}
            {total > 50 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-slate-500">
                  Mostrando {page * 50 + 1} - {Math.min((page + 1) * 50, total)} de {total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * 50 >= total}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Tab: Cargar Excel */}
        <TabsContent value="cargar" className="mt-4">
          {esAdmin && (
          <Card className="p-6" data-testid="padron-importar-card">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Importar Padrón desde Excel</h3>
            
            {/* Drag & Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                isDragging
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
              }`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-4" data-testid="padron-importando">
                  <RefreshCw size={48} className="text-blue-500 animate-spin" />
                  <p className="text-slate-600">Procesando padrón en segundo plano...</p>
                  <p className="text-xs text-slate-400">Esto puede tardar un momento si el archivo es grande. No cierres esta pestaña.</p>
                </div>
              ) : (
                <>
                  <FileSpreadsheet size={48} className="mx-auto text-slate-400 mb-4" />
                  <p className="text-lg font-medium text-slate-700 mb-2">
                    Arrastrá tu archivo Excel aquí
                  </p>
                  <p className="text-slate-500 mb-4">o</p>
                  <div>
                    <input
                      type="file"
                      id="excel-file-input"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={handleFileSelect}
                    />
                    <Button 
                      type="button"
                      onClick={() => document.getElementById('excel-file-input').click()}
                      className="cursor-pointer"
                      data-testid="padron-importar-btn"
                    >
                      <Upload size={16} className="mr-2" />
                      Seleccionar Archivo
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-4">
                    Formatos aceptados: .xlsx, .xls
                  </p>
                </>
              )}
            </div>

            {/* Info de campos */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-700 mb-3">Campos del Excel esperados:</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                {[
                  'PSP / Razón Social',
                  'DNI',
                  'CUIT/CUIL',
                  'Fecha de Nacimiento',
                  'Nacionalidad',
                  'Género',
                  'PEP-UIF',
                  'Tipo',
                  'CBU/CVU',
                  'Fecha de Registración',
                  'Estado',
                  'Fecha de Alta/Baja'
                ].map((campo) => (
                  <div key={campo} className="flex items-center gap-2 text-slate-600">
                    <Check size={14} className="text-green-500" />
                    {campo}
                  </div>
                ))}
              </div>
            </div>

            {/* Notas */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">Notas importantes:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Los usuarios se identifican por <strong>CUIT/CUIL</strong> (clave única)</li>
                <li>• Si un usuario tiene múltiples CVUs, se agrupan automáticamente</li>
                <li>• Los nombres se normalizan (se quitan acentos y diéresis)</li>
                <li>• Si hay cambios en datos existentes (excepto CVU/estado), se marcan para revisión</li>
              </ul>
            </div>
          </Card>
          )}

          {/* Card: Reconciliar con Reporte de Billeteras */}
          <Card className="p-6 mt-6" data-testid="recon-billeteras-card">
            <div className="flex items-start gap-3 mb-4">
              <RefreshCw size={22} className="text-emerald-600 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reconciliar estado de CVUs con Reporte de Billeteras (Waya)</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Subí el <strong>Reporte de billeteras</strong> de Waya (foto completa). Los CVU del padrón que ya
                  no figuren en el reporte se marcan <strong>Inactivos</strong> (re-emitidos/dados de baja), y el resto
                  sincroniza su estado Habilitado/Deshabilitado. Primero muestra un <strong>preview</strong> para revisar.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <input
                type="file"
                id="recon-file-input"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleReconSelect}
                data-testid="recon-file-input"
              />
              <Button
                type="button"
                variant="outline"
                disabled={reconLoading}
                onClick={() => document.getElementById('recon-file-input').click()}
                data-testid="recon-select-btn"
              >
                {reconLoading ? (
                  <><RefreshCw size={16} className="mr-2 animate-spin" /> Procesando…</>
                ) : (
                  <><Upload size={16} className="mr-2" /> Subir Reporte de billeteras (preview)</>
                )}
              </Button>
              {reconFile && !reconLoading && (
                <span className="text-xs text-slate-500" data-testid="recon-file-name">{reconFile.name}</span>
              )}
            </div>

            {reconResult && (
              <div className="mt-5" data-testid="recon-result">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded-lg bg-slate-50 border">
                    <p className="text-xs text-slate-500">CVUs en el reporte</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="recon-total-excel">{reconResult.total_cvus_excel ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                    <p className="text-xs text-rose-600">A inactivar</p>
                    <p className="text-xl font-bold text-rose-700" data-testid="recon-inactivar">{reconResult.cvus_a_inactivar ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <p className="text-xs text-emerald-600">A reactivar</p>
                    <p className="text-xl font-bold text-emerald-700" data-testid="recon-activar">{reconResult.cvus_a_activar ?? 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-50 border">
                    <p className="text-xs text-slate-500">Clientes afectados</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="recon-clientes">{reconResult.clientes_afectados ?? 0}</p>
                  </div>
                </div>

                {(reconResult.muestra_inactivar || []).length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Ejemplos de CVUs que se inactivarían:</p>
                    <div className="max-h-56 overflow-auto rounded-lg border divide-y">
                      {(reconResult.muestra_inactivar || []).slice(0, 30).map((d, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                          <span className="font-mono text-slate-700">{d.cvu}</span>
                          <span className="text-slate-500 truncate ml-3">{d.razon_social || d.cuit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {reconApplied ? (
                  <div className="mt-4 flex items-center gap-2 text-emerald-700 text-sm" data-testid="recon-aplicado">
                    <CheckCircle size={16} /> {reconResult.mensaje}
                  </div>
                ) : (
                  <div className="mt-4 flex items-center gap-3">
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={reconLoading || !reconFile || (reconResult.cvus_a_inactivar === 0 && reconResult.cvus_a_activar === 0)}
                      onClick={() => runReconciliacion(reconFile, false)}
                      data-testid="recon-apply-btn"
                    >
                      {reconLoading ? (
                        <><RefreshCw size={16} className="mr-2 animate-spin" /> Aplicando…</>
                      ) : (
                        <><Check size={16} className="mr-2" /> Aplicar cambios</>
                      )}
                    </Button>
                    <span className="text-xs text-slate-500">Revisá el preview antes de aplicar. Esta acción actualiza el padrón.</span>
                  </div>
                )}
              </div>
            )}
          </Card>
        </TabsContent>

        {esAdmin && (
          <TabsContent value="herramientas-cvu" className="mt-4">
            <HerramientasCvu />
          </TabsContent>
        )}

        {/* Tab: Revisión */}
        <TabsContent value="revision" className="mt-4">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-900">
                Clientes Pendientes de Revisión
                {clientesRevision.length > 0 && (
                  <Badge className="ml-2 bg-amber-500">{clientesRevision.length}</Badge>
                )}
              </h3>
              {clientesRevision.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRecalcularRevisiones}
                  disabled={recalculando}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="recalcular-revisiones-btn"
                  title="Resuelve automáticamente las revisiones que son solo ruido (espacios, mayúsculas o DNI derivado del CUIT)"
                >
                  <RefreshCw size={14} className={`mr-1 ${recalculando ? 'animate-spin' : ''}`} />
                  {recalculando ? 'Limpiando…' : 'Limpiar revisiones triviales'}
                </Button>
              )}
            </div>
            {clientesRevision.length > 0 && (
              <p className="text-sm text-slate-600 mb-4 bg-blue-50 border border-blue-200 rounded-md p-3">
                Estos clientes <strong>ya existían</strong> en el padrón y el Excel trae datos distintos. Revisá las <strong>diferencias</strong> (en rojo el valor actual, en verde el del Excel) y decidí:
                <span className="block mt-1">• <strong className="text-green-700">Aplicar Nuevos</strong>: reemplaza los datos por los del Excel. &nbsp; • <strong className="text-slate-700">Mantener Actuales</strong>: ignora el Excel y deja lo que ya estaba.</span>
                <span className="block mt-1 text-red-700">Los marcados como <strong>"CUIT Faltante"</strong> vienen sin CUIT/CUIL en el Excel y no se pueden cargar: completá el CUIT o eliminá el registro.</span>
              </p>
            )}

            {clientesRevision.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle size={48} className="mx-auto text-green-400 mb-4" />
                <p>No hay clientes pendientes de revisión</p>
              </div>
            ) : (
              <div className="space-y-4">
                {clientesRevision.map((cliente) => (
                  <Card key={cliente.cuit_limpio} className={`p-4 ${!cliente.cuit ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-900">{cliente.razon_social}</h4>
                        {cliente.cuit ? (
                          <p className="text-sm text-slate-600">CUIT: {cliente.cuit}</p>
                        ) : (
                          <p className="text-sm text-red-600 font-medium">
                            ⚠️ Sin CUIT/CUIL - DNI: {cliente.dni || 'No disponible'}
                          </p>
                        )}
                        {cliente.fila_excel && (
                          <p className="text-xs text-slate-500">Fila Excel: {cliente.fila_excel}</p>
                        )}
                      </div>
                      <Badge className={!cliente.cuit ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {!cliente.cuit ? 'CUIT Faltante' : 'Revisar'}
                      </Badge>
                    </div>
                    
                    {/* Mostrar motivo de revisión */}
                    {cliente.motivo_revision && (
                      <div className="mt-3 p-2 bg-white rounded border border-red-200">
                        <p className="text-sm text-red-700">
                          <AlertTriangle size={14} className="inline mr-1" />
                          {cliente.motivo_revision}
                        </p>
                      </div>
                    )}
                    
                    {/* Mostrar datos originales del Excel para registros sin CUIT */}
                    {!cliente.cuit && cliente.datos_originales && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-slate-700 mb-2">Datos del Excel:</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs bg-white p-2 rounded">
                          {Object.entries(cliente.datos_originales).filter(([k, v]) => v && v !== 'nan').slice(0, 6).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-slate-500">{key}:</span>
                              <span className="ml-1 text-slate-700">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {cliente.diferencias_pendientes?.diferencias?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-slate-700 mb-2">Diferencias detectadas (actual → Excel):</p>
                        <div className="space-y-2">
                          {cliente.diferencias_pendientes.diferencias.map((dif, idx) => (
                            <div key={idx} className="flex flex-wrap items-center gap-2 md:gap-4 text-sm bg-white p-2 rounded border border-slate-200">
                              <span className="font-medium text-slate-700 w-full md:w-40">{CAMPO_LABELS[dif.campo] || dif.campo}:</span>
                              <span className="text-red-600 line-through">{dif.valor_actual === null || dif.valor_actual === undefined || dif.valor_actual === '' ? '(vacío)' : String(dif.valor_actual)}</span>
                              <ArrowRight size={14} className="text-slate-400" />
                              <span className="text-green-700 font-medium">{dif.valor_nuevo === null || dif.valor_nuevo === undefined || dif.valor_nuevo === '' ? '(vacío)' : String(dif.valor_nuevo)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fallback: marcado para revisión pero sin motivo ni diferencias guardadas */}
                    {cliente.cuit && !cliente.motivo_revision && !(cliente.diferencias_pendientes?.diferencias?.length > 0) && (
                      <div className="mt-3 p-2 bg-white rounded border border-amber-200">
                        <p className="text-sm text-amber-700">
                          <AlertTriangle size={14} className="inline mr-1" />
                          Marcado para revisión, pero no se registró el detalle de los cambios. Abrí "Ver Detalle" para comparar, o reimportá el Excel.
                        </p>
                      </div>
                    )}
                    
                    <div className="flex gap-2 mt-4">
                      {cliente.cuit ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleResolverRevision(cliente.cuit, 'aplicar_nuevos')}
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check size={14} className="mr-1" />
                            Aplicar Nuevos
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResolverRevision(cliente.cuit, 'mantener_actuales')}
                          >
                            <XCircle size={14} className="mr-1" />
                            Mantener Actuales
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleEliminarSinCuit(cliente.cuit_limpio)}
                        >
                          <XCircle size={14} className="mr-1" />
                          Eliminar Registro
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleVerCliente(cliente)}
                      >
                        <Eye size={14} className="mr-1" />
                        Ver Detalle
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Resultado de Importación */}
      <Dialog open={showImportResult} onOpenChange={setShowImportResult}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600" />
              Resultado de Importación
            </DialogTitle>
          </DialogHeader>
          
          {importResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResult.total_registros}</p>
                  <p className="text-sm text-blue-700">Registros Procesados</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{importResult.clientes_nuevos}</p>
                  <p className="text-sm text-green-700">Clientes Nuevos</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{importResult.clientes_actualizados}</p>
                  <p className="text-sm text-purple-700">Actualizados</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{importResult.para_revision}</p>
                  <p className="text-sm text-amber-700">Para Revisión</p>
                </div>
              </div>
              
              {importResult.errores?.length > 0 && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="font-medium text-red-700 mb-2">
                    {importResult.errores.length} errores encontrados:
                  </p>
                  <div className="max-h-32 overflow-y-auto text-sm text-red-600">
                    {importResult.errores.slice(0, 5).map((err, idx) => (
                      <p key={idx}>Fila {err.fila}: {err.error}</p>
                    ))}
                    {importResult.errores.length > 5 && (
                      <p className="mt-1 text-red-500">... y {importResult.errores.length - 5} más</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowImportResult(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalle de Cliente */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedCliente?.tipo === 'Persona Jurídica' ? (
                <Building2 size={20} className="text-purple-600" />
              ) : (
                <User size={20} className="text-blue-600" />
              )}
              {selectedCliente?.razon_social}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCliente && (
            <div className="space-y-6">
              {/* Datos principales */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">CUIT</p>
                  <div className="flex items-center">
                    <p className="font-mono">{selectedCliente.cuit}</p>
                    <CopyButton value={selectedCliente.cuit} cleanFn={limpiarCuit} />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500">DNI</p>
                  <div className="flex items-center">
                    <p className="font-mono">{selectedCliente.dni || '-'}</p>
                    {selectedCliente.dni && <CopyButton value={selectedCliente.dni} cleanFn={limpiarDni} />}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Tipo</p>
                  <p>{selectedCliente.tipo}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Nacionalidad</p>
                  <p>{selectedCliente.nacionalidad || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Género</p>
                  <p>{selectedCliente.genero === 'M' ? 'Masculino' : selectedCliente.genero === 'F' ? 'Femenino' : selectedCliente.genero || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Fecha Nacimiento</p>
                  <p>{selectedCliente.fecha_nacimiento || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">PEP-UIF</p>
                  {selectedCliente.pep_uif ? (
                    <Badge className="bg-red-100 text-red-700">Sí - PEP</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-700">No</Badge>
                  )}
                </div>
              </div>

              {/* CVUs */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CreditCard size={18} className="text-blue-600" />
                  CVUs ({selectedCliente.cvus?.length || 0})
                </h4>
                
                {selectedCliente.cvus?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedCliente.cvus.map((cvu, idx) => (
                      <Card key={idx} className={`p-4 ${cvu.activo ? 'border-green-200 bg-green-50/50' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-mono text-sm">{cvu.numero}</p>
                              <CopyButton value={cvu.numero} cleanFn={limpiarCvu} />
                              {cvu.activo ? (
                                <Badge className="bg-green-100 text-green-700">Activo</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600">Inactivo</Badge>
                              )}
                            </div>
                            <div className="flex gap-4 mt-2 text-xs text-slate-500">
                              <span>Registrado: {cvu.fecha_registracion || '-'}</span>
                              <span>Estado desde: {cvu.fecha_estado || '-'}</span>
                            </div>
                            
                            {/* Comentario */}
                            <div className="mt-3">
                              <Textarea
                                placeholder="Agregar comentario interno..."
                                className="text-sm h-16"
                                defaultValue={cvu.comentarios || ''}
                                onBlur={(e) => handleActualizarComentario(selectedCliente.cuit, cvu.numero, e.target.value)}
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            <span className="text-xs text-slate-500">{cvu.activo ? 'Activo' : 'Inactivo'}</span>
                            <Switch
                              checked={cvu.activo}
                              onCheckedChange={(checked) => handleCambiarEstadoCvu(selectedCliente.cuit, cvu.numero, checked)}
                            />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No hay CVUs registrados</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PadronInterno;
